/*
  Session product page sync. The theme's own product script picks the variant
  (option clicks, price, gallery, URL) and stamps its id on the product element
  as data-product-variant-id. This watches that attribute and updates the
  Session-only parts from the per-variant JSON (snippets/session-product-main):
  title suffix, option labels, per-shape/scent text ([data-session-for]),
  "Add to bag · price" / Sold out, the Amazon link and the scent photo tint.
  Also drives the quantity − / + stepper (it fires "change" on the field so the
  bundle section can follow), the note under Add to bag (its quantity 2+ text),
  and the thumbnail column's arrows.
*/
if (!customElements.get('session-product-sync')) {
  class SessionProductSync extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.product = this.closest('[data-js-product]');
      if (!this.product) return;

      const json = this.product.querySelector('[data-session-product-json]');
      try {
        this.data = json ? JSON.parse(json.textContent) : { variants: {} };
      } catch (error) {
        this.data = { variants: {} };
      }

      this.bindQuantity();
      this.bindThumbs();
      this.observer = new MutationObserver(() => this.update());
      this.observer.observe(this.product, { attributes: true, attributeFilter: ['data-product-variant-id'] });
      this.update();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    bindQuantity() {
      this.product.querySelectorAll('[data-session-qty]').forEach((button) => {
        button.addEventListener('click', () => {
          const input = button.parentElement.querySelector('input');
          if (!input) return;
          const next = (parseInt(input.value, 10) || 1) + Number(button.dataset.sessionQty);
          input.value = Math.min(99, Math.max(1, next));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      this.product.querySelectorAll('.session-pdp__qty-input').forEach((input) => {
        input.addEventListener('input', () => this.updateNote(input));
        input.addEventListener('change', () => this.updateNote(input));
      });
    }

    // "Add a 2nd item and save 10%." turns into its quantity 2+ text (block setting).
    updateNote(input) {
      const note = this.product.querySelector('[data-session-note]');
      if (!note) return;
      const multi = (parseInt(input.value, 10) || 1) >= 2;
      note.textContent = multi ? note.dataset.multi : note.dataset.one;
    }

    // Thumbnail strip: click → move the theme's slider; highlight the slide in view.
    // It shows 5 at a time (a column on desktop, a row on tablet); the arrows page
    // through the rest. On mobile it is a swipeable row without arrows.
    bindThumbs() {
      this.thumbs = this.product.querySelector('[data-session-thumbs]');
      this.gallery = this.product.querySelector('product-gallery');
      if (!this.thumbs || !this.gallery) return;

      this.thumbsNav = this.product.querySelector('[data-session-thumbs-nav]');
      if (this.thumbsNav) {
        this.thumbsNav.addEventListener('click', (event) => {
          const arrow = event.target.closest('[data-session-thumbs-step]');
          if (!arrow) return;
          // One page = the visible strip plus the 8px gap before the next thumb.
          const step = Number(arrow.dataset.sessionThumbsStep);
          const behavior = this.scrollBehavior();
          if (this.isRow()) this.thumbs.scrollBy({ left: step * (this.thumbs.clientWidth + 8), behavior });
          else this.thumbs.scrollBy({ top: step * (this.thumbs.clientHeight + 8), behavior });
        });
        this.thumbs.addEventListener('scroll', () => this.updateThumbArrows(), { passive: true });
        new ResizeObserver(() => this.updateThumbArrows()).observe(this.thumbs);
      }

      this.thumbs.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-media-id]');
        if (!thumb || typeof this.gallery.goToSlideById !== 'function') return;
        const group = this.data.shapeGallery ? this.currentGroup() : false;
        this.gallery.goToSlideById(Number(thumb.dataset.mediaId), group);
      });

      const slider = this.gallery.querySelector('[data-js-product-gallery-main-slider]');
      if (slider) {
        new MutationObserver(() => this.markActiveThumb()).observe(slider, {
          subtree: true,
          attributes: true,
          attributeFilter: ['class'],
        });
      }
      this.markActiveThumb();
    }

    scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    isRow() {
      return getComputedStyle(this.thumbs).flexDirection === 'row';
    }

    updateThumbArrows() {
      if (!this.thumbsNav) return;
      const strip = this.thumbs;
      const row = this.isRow();
      const size = row ? strip.clientWidth : strip.clientHeight;
      const full = row ? strip.scrollWidth : strip.scrollHeight;
      const position = row ? strip.scrollLeft : strip.scrollTop;
      this.thumbsNav.hidden = full - size <= 1;
      const [prev, next] = this.thumbsNav.querySelectorAll('[data-session-thumbs-step]');
      if (prev) prev.disabled = position <= 1;
      if (next) next.disabled = position + size >= full - 1;
    }

    currentGroup() {
      const variant = this.data.variants[this.product.getAttribute('data-product-variant-id')];
      return variant ? variant.group : false;
    }

    markActiveThumb() {
      if (!this.thumbs) return;
      const current = this.gallery.querySelector('.slick-current:not(.slick-cloned)[data-media-id], .slick-current:not(.slick-cloned) [data-media-id]');
      const id = current ? current.getAttribute('data-media-id') : null;
      if (!id || id === this.activeMediaId) return;
      this.activeMediaId = id;
      this.thumbs.querySelectorAll('[data-media-id]').forEach((thumb) => {
        const active = thumb.dataset.mediaId === id;
        thumb.classList.toggle('session-pdp__thumb--active', active);
        if (active && !thumb.hidden) this.revealThumb(thumb);
      });
    }

    // Scroll the strip (column or row) just enough to show the thumb.
    revealThumb(thumb) {
      const strip = this.thumbs;
      const box = strip.getBoundingClientRect();
      const rect = thumb.getBoundingClientRect();
      const behavior = this.scrollBehavior();
      if (this.isRow()) {
        // The mobile row has side padding so it can run edge to edge.
        const pad = parseFloat(getComputedStyle(strip).paddingLeft) || 0;
        const left = rect.left < box.left + pad ? rect.left - box.left - pad : rect.right > box.right - pad ? rect.right - box.right + pad : 0;
        if (left) strip.scrollBy({ left, behavior });
      } else {
        const top = rect.top < box.top ? rect.top - box.top : rect.bottom > box.bottom ? rect.bottom - box.bottom : 0;
        if (top) strip.scrollBy({ top, behavior });
      }
    }

    update() {
      const variant = this.data.variants[this.product.getAttribute('data-product-variant-id')];
      if (!variant) return;
      const root = this.product;

      // A thumb's group lists the ids of the variants it shows for. As in the theme's
      // gallery, a variant without photos of its own shows them all.
      if (this.thumbs && this.data.shapeGallery) {
        const thumbs = this.thumbs.querySelectorAll('[data-group]');
        const inGroup = (thumb) => thumb.dataset.group.split(' ').includes(variant.group);
        const grouped = Array.from(thumbs).some(inGroup);
        thumbs.forEach((thumb) => {
          thumb.hidden = grouped && !inGroup(thumb) && thumb.dataset.group !== 'video_group';
        });
        this.activeMediaId = null;
        this.thumbs.scrollTo({ top: 0, left: 0 });
        this.updateThumbArrows();
        requestAnimationFrame(() => this.markActiveThumb());
      }

      root.querySelectorAll('[data-session-suffix]').forEach((el) => (el.textContent = variant.suffix));

      root.querySelectorAll('[data-session-option-label]').forEach((el) => {
        const label = variant.labels[Number(el.dataset.sessionOptionLabel)];
        if (label != null) el.textContent = label;
      });

      // data-session-for holds one handle, or several separated by spaces.
      root.querySelectorAll('[data-session-for]').forEach((el) => {
        el.hidden = !el.dataset.sessionFor.split(' ').some((handle) => variant.options.includes(handle));
      });

      const button = root.querySelector('[data-session-submit]');
      if (button) {
        const label = variant.available ? `Add to bag · ${variant.price}` : 'Sold out';
        button.dataset.label = label;
        button.toggleAttribute('data-unavailable', !variant.available);
        if (!button.hasAttribute('data-busy')) {
          button.disabled = !variant.available;
          const text = button.querySelector('[data-session-submit-text]');
          if (text) text.textContent = label;
        }
      }

      const amazon = root.querySelector('[data-session-amazon]');
      if (amazon) {
        const href = variant.amazon || amazon.dataset.defaultHref;
        if (href) {
          amazon.href = href;
          amazon.hidden = false;
        } else {
          amazon.removeAttribute('href');
          amazon.hidden = true;
        }
      }

      if (variant.background) root.style.setProperty('--session-pdp-media-bg', variant.background);
      else root.style.removeProperty('--session-pdp-media-bg');
    }
  }

  customElements.define('session-product-sync', SessionProductSync);
}
